import { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Music2, Pause, Play, Mic2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function fmtTime(secs) {
  if (!secs && secs !== 0) return '';
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
}

export default function NowPlayingWidget() {
  const { guildId } = useParams();
  const [searchParams] = useSearchParams();
  const theme = searchParams.get('theme') || 'dark';
  const [data, setData] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const tickRef = useRef(null);
  const [lyrics, setLyrics] = useState(null);
  const lyricsBoxRef = useRef(null);
  const lineRefs = useRef([]);
  const [showLyrics, setShowLyrics] = useState(() => {
    if (searchParams.get('lyrics') === '1') return true;
    try { return localStorage.getItem('widget_lyrics') !== '0'; } catch { return true; }
  });
  const toggleLyrics = () => {
    setShowLyrics(v => {
      try { localStorage.setItem('widget_lyrics', v ? '0' : '1'); } catch {}
      return !v;
    });
  };

  useEffect(() => {
    const load = () => {
      axios.get(`${API}/api/widget/${guildId}/now-playing`)
        .then(r => {
          setData(r.data);
          setElapsed(r.data.elapsed || 0);
        })
        .catch(() => {});
    };
    load();
    const interval = setInterval(load, 10000);
    return () => clearInterval(interval);
  }, [guildId]);

  useEffect(() => {
    if (tickRef.current) clearInterval(tickRef.current);
    if (data?.playing && !data?.paused) {
      tickRef.current = setInterval(() => setElapsed(e => e + 1), 1000);
    }
    return () => clearInterval(tickRef.current);
  }, [data?.playing, data?.paused, data?.title]);

  const trackTitle = data?.playing ? data.title : null;
  useEffect(() => {
    setLyrics(null);
    if (!trackTitle) return undefined;
    let alive = true;
    axios.get(`${API}/api/widget/${guildId}/lyrics`)
      .then(r => { if (alive) setLyrics(r.data); })
      .catch(() => {});
    return () => { alive = false; };
  }, [guildId, trackTitle]);

  const synced = useMemo(() => (lyrics?.found && Array.isArray(lyrics.synced) ? lyrics.synced : []), [lyrics]);
  const hasLyrics = !!(lyrics?.found && (synced.length > 0 || lyrics.plain));
  let activeIdx = -1;
  for (let i = 0; i < synced.length; i++) { if (synced[i].t <= elapsed) activeIdx = i; else break; }

  useEffect(() => {
    if (!showLyrics || activeIdx < 0) return;
    const box = lyricsBoxRef.current;
    const el = lineRefs.current[activeIdx];
    if (box && el) box.scrollTo({ top: el.offsetTop - box.clientHeight / 2 + el.clientHeight / 2, behavior: 'smooth' });
  }, [activeIdx, showLyrics]);

  const isDark = theme !== 'light';
  const bg = isDark ? '#0a0a0a' : '#f4f4f5';
  const text = isDark ? '#ffffff' : '#09090b';
  const sub = isDark ? '#71717a' : '#52525b';
  const border = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)';
  const accent = '#5865F2';

  const progress = data?.duration && elapsed ? Math.min(1, elapsed / data.duration) : 0;

  return (
    <div
      data-testid="widget-root"
      style={{
        minHeight: '100vh', background: bg, display: 'flex',
        alignItems: 'center', justifyContent: 'center',
        fontFamily: "'Manrope', sans-serif", padding: '16px',
      }}
    >
      <div
        style={{
          width: '100%', maxWidth: 360,
          background: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.05)',
          border: `1px solid ${border}`, borderRadius: 16, padding: 16,
          boxSizing: 'border-box',
        }}
      >
        {!data?.playing ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 48, height: 48, borderRadius: 8, background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Music2 size={22} color={sub} />
            </div>
            <p style={{ color: sub, fontSize: 13, margin: 0 }}>Nothing playing right now</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
              {data.thumbnail ? (
                <img src={data.thumbnail} alt="" style={{ width: 52, height: 52, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }} />
              ) : (
                <div style={{ width: 52, height: 52, borderRadius: 8, background: `${accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Music2 size={22} color={accent} />
                </div>
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ color: text, fontWeight: 600, fontSize: 14, margin: '0 0 2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {data.title}
                </p>
                {data.uploader && (
                  <p style={{ color: sub, fontSize: 12, margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {data.uploader}
                  </p>
                )}
              </div>
              {hasLyrics && (
                <button
                  type="button" onClick={toggleLyrics} title={showLyrics ? 'Hide lyrics' : 'Show lyrics'}
                  style={{ background: showLyrics ? `${accent}30` : 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer', padding: '2px 4px', flexShrink: 0, display: 'flex' }}
                >
                  <Mic2 size={15} color={showLyrics ? accent : sub} />
                </button>
              )}
              {data.paused
                ? <Pause size={16} color={sub} style={{ flexShrink: 0 }} />
                : <Play size={16} color={accent} style={{ flexShrink: 0 }} />
              }
            </div>

            {data.duration && (
              <>
                <div style={{ height: 3, background: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.12)', borderRadius: 2, overflow: 'hidden', marginBottom: 6 }}>
                  <div style={{ height: '100%', width: `${progress * 100}%`, background: accent, borderRadius: 2, transition: 'width 1s linear' }} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: sub, fontSize: 11 }}>{fmtTime(elapsed)}</span>
                  <span style={{ color: sub, fontSize: 11 }}>{fmtTime(data.duration)}</span>
                </div>
              </>
            )}

            {hasLyrics && showLyrics && (
              <div
                ref={lyricsBoxRef}
                data-testid="widget-lyrics"
                style={{ marginTop: 12, height: synced.length > 0 ? 110 : 120, overflowY: 'auto', scrollbarWidth: 'none', textAlign: 'center', position: 'relative' }}
              >
                {synced.length > 0 ? (
                  <div style={{ padding: '40px 0' }}>
                    {synced.map((l, i) => (
                      <p
                        key={i}
                        ref={el => { lineRefs.current[i] = el; }}
                        style={{
                          margin: '0 0 6px', fontSize: i === activeIdx ? 14 : 12.5, lineHeight: 1.35,
                          fontWeight: i === activeIdx ? 700 : 500,
                          color: i === activeIdx ? text : sub, opacity: i === activeIdx ? 1 : 0.65,
                          transition: 'all 0.3s ease',
                        }}
                      >
                        {l.text || '♪'}
                      </p>
                    ))}
                  </div>
                ) : (
                  <p style={{ color: sub, fontSize: 12.5, lineHeight: 1.5, whiteSpace: 'pre-wrap', margin: 0 }}>{lyrics.plain}</p>
                )}
              </div>
            )}

            {data.queue_length > 0 && (
              <p style={{ color: sub, fontSize: 11, marginTop: 8, margin: '8px 0 0' }}>
                +{data.queue_length} in queue
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
